import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { Header } from '../components/Header';
import { LocationPicker } from '../components/LocationPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { violationService } from '../services/api';
import { prepareViolationImage, getCurrentCoords, inspectImageEvidence } from '../lib/imageCapture';
import { 
    Upload, 
    Car, 
    FileText, 
    Clock, 
    CheckCircle2, 
    XCircle,
    Loader2,
    Image as ImageIcon,
    Video,
    X,
    AlertTriangle,
    ArrowRight,
    Camera
} from 'lucide-react';

// File size limits
// Images are auto-compressed under the 2MB backend cap in lib/imageCapture.js,
// so there is no hard image-size constant here — only the video limit applies.
const MAX_VIDEO_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_IMAGES = 5;
const REPORT_MAX_AGE_HOURS = 48;

/** Format a Date as a `datetime-local` input value (YYYY-MM-DDTHH:mm) in local time. */
const toLocalInputValue = (d) => {
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

export default function UserDashboard() {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    useInactivityLogout(); // auto-logout after 5 min idle
    const [violations, setViolations] = useState([]);
    const [violationTypes, setViolationTypes] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(null);

    // Form state
    const [vehicleNumber, setVehicleNumber] = useState('');
    const [violationType, setViolationType] = useState('');
    const [violationDateTime, setViolationDateTime] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [images, setImages] = useState([]);
    const [imageMeta, setImageMeta] = useState([]); // EXIF inspection, aligned with `images`
    const [video, setVideo] = useState(null);
    const [processingImages, setProcessingImages] = useState(false);

    const fetchViolations = useCallback(async () => {
        try {
            const response = await violationService.list();
            setViolations(response.data);
        } catch (error) {
            console.error('Failed to fetch violations');
        }
    }, [token]);

    const fetchViolationTypes = useCallback(async () => {
        try {
            const response = await violationService.getTypes();
            setViolationTypes(response.data);
        } catch (error) {
            console.error('Failed to fetch violation types');
        }
    }, []);

    useEffect(() => {
        fetchViolations();
        fetchViolationTypes();
    }, [fetchViolations, fetchViolationTypes]);

    // `fromCamera` distinguishes the in-app Capture button from a gallery Upload.
    // Only the capture flow stamps GPS/timestamp; gallery files are taken as-is and
    // it is the user's responsibility that their media already carries that metadata.
    const handleImageChange = async (e, fromCamera = false) => {
        const files = Array.from(e.target.files || []);
        // Reset the input so re-selecting the same file fires onChange again.
        e.target.value = '';
        if (files.length === 0) return;

        setProcessingImages(true);
        try {
            // Only request device GPS for the capture flow; gallery uploads never
            // prompt for location and are never GPS-stamped.
            let coords = null;
            if (fromCamera) {
                coords = await getCurrentCoords();
                if (!coords) {
                    toast.warning(
                        'Location unavailable — enable location access so captured photos are GPS-stamped, otherwise the report may be rejected.',
                        { duration: 7000 }
                    );
                }
            }

            const processed = [];
            const processedMeta = [];
            const skipped = [];
            let stampedAny = false;

            for (const f of files) {
                if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
                    skipped.push(`${f.name}: Invalid format`);
                    continue;
                }
                try {
                    // Capture: compress + stamp GPS/timestamp. Gallery: compress only,
                    // preserving the photo's own EXIF without fabricating anything.
                    // eslint-disable-next-line no-await-in-loop
                    const { file: prepared, stampedGps } = await prepareViolationImage(f, coords, { stamp: fromCamera });
                    // Read GPS + timestamp from what will actually be uploaded, and
                    // check the 48-hour window against the photo's own capture time.
                    // eslint-disable-next-line no-await-in-loop
                    const meta = await inspectImageEvidence(prepared, REPORT_MAX_AGE_HOURS);
                    processed.push(prepared);
                    processedMeta.push(meta);
                    if (stampedGps) stampedAny = true;
                } catch (err) {
                    console.error('Image processing failed', err);
                    skipped.push(`${f.name}: Could not process image`);
                }
            }

            if (skipped.length > 0) {
                toast.error(`Skipped: ${skipped.join(', ')}`);
            }
            if (stampedAny) {
                toast.success('GPS & timestamp added to captured photo(s).');
            }

            // Proactively warn about evidence read from this batch (does not block submit).
            const batchStale = processedMeta.some(m => m.hasTimestamp && m.withinWindow === false);
            const batchMissing = processedMeta.some(m => !m.hasGps || !m.hasTimestamp);
            if (batchStale) {
                toast.error(
                    `Some evidence appears older than ${REPORT_MAX_AGE_HOURS} hours. Violations must be reported within ${REPORT_MAX_AGE_HOURS} hours of the occurrence.`,
                    { duration: 8000 }
                );
            }
            if (batchMissing) {
                toast.warning(
                    'Some evidence is missing GPS/timestamp — this violation may be rejected during review.',
                    { duration: 8000 }
                );
            }

            setImages(prev => [...prev, ...processed].slice(0, MAX_IMAGES));
            setImageMeta(prev => [...prev, ...processedMeta].slice(0, MAX_IMAGES));
        } finally {
            setProcessingImages(false);
        }
    };

    const handleVideoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) {
                toast.error('Only MP4/WebM videos are allowed');
                return;
            }
            if (file.size > MAX_VIDEO_SIZE) {
                toast.error('Video must be under 20MB');
                return;
            }
            setVideo(file);
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImageMeta(prev => prev.filter((_, i) => i !== index));
    };

    const handleLocationSelect = ({ latitude: lat, longitude: lng, locationName }) => {
        setLatitude(lat);
        setLongitude(lng);
        setLocation(locationName);
    };

    const handleSubmit = async (e, skipDuplicateCheck = false) => {
        e?.preventDefault();
        
        if (!vehicleNumber || !violationType) {
            toast.error('Please fill in required fields');
            return;
        }

        if (!violationDateTime) {
            toast.error('Please select the date & time the violation occurred');
            return;
        }
        const occurred = new Date(violationDateTime);
        if (Number.isNaN(occurred.getTime())) {
            toast.error('Invalid violation date & time');
            return;
        }
        if (occurred.getTime() > Date.now() + 5 * 60000) {
            toast.error('Violation date & time cannot be in the future');
            return;
        }
        if (Date.now() - occurred.getTime() > REPORT_MAX_AGE_HOURS * 3600 * 1000) {
            toast.error(`Violations must be reported within ${REPORT_MAX_AGE_HOURS} hours of occurrence`);
            return;
        }

        if (images.length === 0 && !video) {
            toast.error('Please upload at least one image or video');
            return;
        }

        setSubmitting(true);
        setDuplicateWarning(null);
        
        try {
            const formData = new FormData();
            formData.append('vehicle_number', vehicleNumber);
            formData.append('violation_type', violationType);
            formData.append('violation_datetime', occurred.toISOString());
            formData.append('violation_local', violationDateTime); // raw wall-clock for EXIF cross-check
            if (description) formData.append('description', description);
            if (location) formData.append('location', location);
            if (latitude) formData.append('latitude', latitude);
            if (longitude) formData.append('longitude', longitude);
            if (skipDuplicateCheck) formData.append('skip_duplicate_check', 'true');
            
            images.forEach(img => formData.append('images', img));
            if (video) formData.append('video', video);

            const response = await violationService.create(formData);

            // Check for duplicate warning
            if (response.data.warning === 'potential_duplicate') {
                setDuplicateWarning(response.data);
                setSubmitting(false);
                return;
            }

            toast.success('Violation reported successfully!');
            
            if (response.data.plate_match_status === 'Mismatch') {
                toast.warning(
                    `OCR mismatch: Image plate "${response.data.ocr_detected_plate}" differs from entered "${vehicleNumber}". Officer will review.`,
                    { duration: 7000 }
                );
            } else if (response.data.ocr_detected_plate) {
                toast.info(`License plate detected: ${response.data.ocr_detected_plate}`, {
                    duration: 5000
                });
            }
            
            if (response.data.skipped_files?.length > 0) {
                toast.warning(`Some files were skipped: ${response.data.skipped_files.map(f => f.reason).join(', ')}`);
            }

            // Reset form
            setVehicleNumber('');
            setViolationType('');
            setViolationDateTime('');
            setDescription('');
            setLocation('');
            setLatitude(null);
            setLongitude(null);
            setImages([]);
            setImageMeta([]);
            setVideo(null);
            setDuplicateWarning(null);
            
            // Refresh list
            fetchViolations();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.message) {
                const extra = detail.details?.length ? ` (${detail.details.join('; ')})` : '';
                toast.error(`${detail.message}${extra}`, { duration: 8000 });
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Failed to submit violation');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const stats = {
        total: violations.length,
        pending: violations.filter(v => v.status === 'pending').length,
        approved: violations.filter(v => v.status === 'approved').length,
        rejected: violations.filter(v => v.status === 'rejected').length
    };

    // Evidence-quality signals read from uploaded photos' EXIF (advisory, non-blocking).
    const staleEvidence = imageMeta.filter(m => m.hasTimestamp && m.withinWindow === false).length;
    const missingMetaEvidence = imageMeta.filter(m => !m.hasGps || !m.hasTimestamp).length;
    const showEvidenceNotice = staleEvidence > 0 || missingMetaEvidence > 0;

    // Per-photo badge status + tooltip.
    const evidenceStatus = (m) => {
        if (!m) return null;
        const bad = (m.hasTimestamp && m.withinWindow === false) || !m.hasGps || !m.hasTimestamp;
        return bad ? 'warn' : 'ok';
    };
    const evidenceTitle = (m) => {
        if (!m) return '';
        const issues = [];
        if (!m.hasTimestamp) issues.push('no timestamp');
        if (!m.hasGps) issues.push('no GPS');
        if (m.hasTimestamp && m.withinWindow === false) issues.push(`older than ${REPORT_MAX_AGE_HOURS}h`);
        return issues.length ? `May be rejected — ${issues.join(', ')}` : 'GPS & timestamp OK';
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Welcome */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">
                        Welcome, {user?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Report traffic violations and help make roads safer
                    </p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                    <Card 
                        className="cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => navigate('/reports')}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                    <p className="text-xs text-muted-foreground">Total Reports</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card 
                        className="cursor-pointer hover:border-warning/50 transition-colors"
                        onClick={() => navigate('/reports')}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-warning" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                    <p className="text-xs text-muted-foreground">Pending</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card 
                        className="cursor-pointer hover:border-green-500/50 transition-colors"
                        onClick={() => navigate('/reports')}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.approved}</p>
                                    <p className="text-xs text-muted-foreground">Approved</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card 
                        className="cursor-pointer hover:border-red-500/50 transition-colors"
                        onClick={() => navigate('/reports')}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                    <XCircle className="w-5 h-5 text-red-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.rejected}</p>
                                    <p className="text-xs text-muted-foreground">Rejected</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* View Reports Link */}
                <div className="mb-6">
                    <Button 
                        variant="outline" 
                        onClick={() => navigate('/reports')}
                        className="gap-2"
                        data-testid="view-reports-btn"
                    >
                        <FileText className="w-4 h-4" />
                        View All My Reports
                        <ArrowRight className="w-4 h-4" />
                    </Button>
                </div>

                {/* Report Form */}
                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Report New Violation
                        </CardTitle>
                        <CardDescription>
                            Submit a traffic violation report with evidence
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="vehicle-number">
                                        Vehicle Number <span className="text-destructive">*</span>
                                    </Label>
                                    <div className="relative">
                                        <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="vehicle-number"
                                            placeholder="MH12AB1234"
                                            value={vehicleNumber}
                                            onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                                            className="pl-10 font-mono"
                                            required
                                            data-testid="vehicle-number-input"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Indian format: XX00XX0000
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>
                                        Violation Type <span className="text-destructive">*</span>
                                    </Label>
                                    <Select
                                        value={violationType}
                                        onValueChange={setViolationType}
                                        required
                                    >
                                        <SelectTrigger data-testid="violation-type-select">
                                            <SelectValue placeholder="Select violation type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {violationTypes.map(type => (
                                                <SelectItem key={type.id} value={type.id}>
                                                    {type.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="violation-datetime">
                                    Date &amp; Time of Violation <span className="text-destructive">*</span>
                                </Label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                    <Input
                                        id="violation-datetime"
                                        type="datetime-local"
                                        value={violationDateTime}
                                        onChange={(e) => setViolationDateTime(e.target.value)}
                                        min={toLocalInputValue(new Date(Date.now() - REPORT_MAX_AGE_HOURS * 3600 * 1000))}
                                        max={toLocalInputValue(new Date())}
                                        className="pl-10"
                                        required
                                        data-testid="violation-datetime-input"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    When did it happen? Must be within the last {REPORT_MAX_AGE_HOURS} hours.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Location (with Map)</Label>
                                <LocationPicker
                                    onLocationSelect={handleLocationSelect}
                                    initialLocationName={location}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    placeholder="Describe the violation..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    data-testid="description-input"
                                />
                            </div>

                            {/* Duplicate Warning */}
                            {duplicateWarning && (
                                <Alert variant="destructive" className="bg-warning/10 border-warning/30">
                                    <AlertTriangle className="h-4 w-4 text-warning" />
                                    <AlertTitle className="text-warning">Potential Duplicate</AlertTitle>
                                    <AlertDescription className="text-sm">
                                        {duplicateWarning.message}
                                        <div className="mt-2 flex gap-2">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setDuplicateWarning(null)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={() => handleSubmit(null, true)}
                                                disabled={submitting}
                                            >
                                                Submit Anyway
                                            </Button>
                                        </div>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Image Upload */}
                            <div className="space-y-2">
                                <Label>
                                    Images <span className="text-destructive">*</span>
                                </Label>
                                <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        multiple
                                        onChange={(e) => handleImageChange(e, false)}
                                        className="hidden"
                                        id="image-upload"
                                        data-testid="image-upload-input"
                                    />
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        capture="environment"
                                        onChange={(e) => handleImageChange(e, true)}
                                        className="hidden"
                                        id="image-capture"
                                        data-testid="image-capture-input"
                                    />
                                    {processingImages ? (
                                        <Loader2 className="w-8 h-8 mx-auto text-primary mb-2 animate-spin" />
                                    ) : (
                                        <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                    )}
                                    <div className="flex flex-col sm:flex-row gap-2 justify-center mb-2">
                                        <label
                                            htmlFor="image-upload"
                                            aria-disabled={processingImages}
                                            className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors ${processingImages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                                            data-testid="image-upload-btn"
                                        >
                                            <Upload className="w-3.5 h-3.5" />
                                            Upload
                                        </label>
                                        <label
                                            htmlFor="image-capture"
                                            aria-disabled={processingImages}
                                            className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors ${processingImages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                                            data-testid="image-capture-btn"
                                        >
                                            <Camera className="w-3.5 h-3.5" />
                                            Capture Photo
                                        </label>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {processingImages
                                            ? 'Processing photo…'
                                            : `Max ${MAX_IMAGES} images, auto-compressed under 2MB. Capture stamps GPS & time automatically; uploaded files must already contain GPS & timestamp.`}
                                    </p>
                                </div>
                                {images.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {images.map((img, idx) => {
                                            const st = evidenceStatus(imageMeta[idx]);
                                            return (
                                                <div key={idx} className="relative w-16 h-16">
                                                    <img
                                                        src={URL.createObjectURL(img)}
                                                        alt={`Preview ${idx}`}
                                                        className="w-full h-full object-cover rounded"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImage(idx)}
                                                        className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                    {st && (
                                                        <span
                                                            title={evidenceTitle(imageMeta[idx])}
                                                            className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-background ${
                                                                st === 'ok' ? 'bg-success text-white' : 'bg-warning text-warning-foreground'
                                                            }`}
                                                            data-testid={`evidence-badge-${idx}`}
                                                        >
                                                            {st === 'ok'
                                                                ? <CheckCircle2 className="w-3 h-3" />
                                                                : <AlertTriangle className="w-3 h-3" />}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Video Upload */}
                            <div className="space-y-2">
                                <Label>Video (Optional)</Label>
                                <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                                    <input
                                        type="file"
                                        accept="video/mp4,video/webm,video/quicktime"
                                        onChange={handleVideoChange}
                                        className="hidden"
                                        id="video-upload"
                                        data-testid="video-upload-input"
                                    />
                                    <input
                                        type="file"
                                        accept="video/mp4,video/webm,video/quicktime"
                                        capture="environment"
                                        onChange={handleVideoChange}
                                        className="hidden"
                                        id="video-capture"
                                        data-testid="video-capture-input"
                                    />
                                    {video ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <Video className="w-5 h-5 text-primary" />
                                            <span className="text-sm">{video.name}</span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setVideo(null);
                                                }}
                                                className="text-destructive"
                                                data-testid="remove-video-btn"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <Video className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                            <div className="flex flex-col sm:flex-row gap-2 justify-center mb-2">
                                                <label
                                                    htmlFor="video-upload"
                                                    className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                                                    data-testid="video-upload-btn"
                                                >
                                                    <Upload className="w-3.5 h-3.5" />
                                                    Upload
                                                </label>
                                                <label
                                                    htmlFor="video-capture"
                                                    className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                                    data-testid="video-capture-btn"
                                                >
                                                    <Camera className="w-3.5 h-3.5" />
                                                    Record Video
                                                </label>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Max 20MB. Videos must include creation timestamp.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Evidence-quality notice — advisory only, never blocks submission */}
                            {showEvidenceNotice && (
                                <Alert variant="destructive" className="bg-warning/10 border-warning/30" data-testid="evidence-notice">
                                    <AlertTriangle className="h-4 w-4 text-warning" />
                                    <AlertTitle className="text-warning">Before you submit — check your evidence</AlertTitle>
                                    <AlertDescription className="text-sm">
                                        <ul className="list-disc list-inside space-y-1">
                                            {staleEvidence > 0 && (
                                                <li>
                                                    {staleEvidence} photo{staleEvidence > 1 ? 's appear' : ' appears'} older than {REPORT_MAX_AGE_HOURS} hours.
                                                    Violations must be reported within {REPORT_MAX_AGE_HOURS} hours of the occurrence.
                                                </li>
                                            )}
                                            {missingMetaEvidence > 0 && (
                                                <li>
                                                    {missingMetaEvidence} photo{missingMetaEvidence > 1 ? 's are' : ' is'} missing GPS/timestamp,
                                                    so this violation may be rejected during review.
                                                </li>
                                            )}
                                        </ul>
                                        <p className="mt-2 text-muted-foreground">
                                            You can still submit — an officer will make the final decision.
                                        </p>
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={submitting || processingImages}
                                data-testid="submit-violation-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Submitting...
                                    </>
                                ) : processingImages ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Processing photos...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Submit Report
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
